import React, { useRef, useEffect, useState } from 'react';
import './TimelineSection.css';
import TimelineItem from './TimelineItem';
import Container from '../Container/Container';

interface Experience {
  title: string;
  company: string;
  period: string;
  description: string[];
  technologies?: string[];
}

const experiences: Experience[] = [
  {
    title: "Freelance Software Engineer",
    company: "Self-Employed",
    period: "2024 - Present",
    description: [
      "Architected and delivered four production Next.js sites, including a dual-dashboard platform (customer + admin) for Brandica, plus Sahara Sweets and CowboyRX",
      "Built an end-to-end online ordering system (menu, cart/checkout, order lifecycle, and real-time notifications) backed by a custom AWS serverless API",
      "Designed and shipped a lightweight TypeScript SDK (~10kb) to embed dynamic dispensary menus and ordering flows into external websites"
    ],
    technologies: ["NextJS", "React", "TypeScript", "AWS Lambda", "DynamoDB", "Typesense", "Serverless"]
  },
  {
    title: "Senior E-Commerce Engineer",
    company: "Crimson Agility",
    period: "2024",
    description: [
      "Led a Shopify project integrating front-end (Liquid) and back-end (JavaScript) solutions on Google Cloud Platform",
      "Built middleware connecting Shopify to a 3rd-party service for subscription management, dynamically tailoring offerings",
      "Processed over 1M records from CSV files into structured tables for efficient retrieval"
    ],
    technologies: ["Liquid", "JavaScript", "Shopify", "Google Cloud Platform"]
  },
  {
    title: "Software Engineer II",
    company: "Extend",
    period: "2021 - 2023",
    description: [
      "Architected scalable serverless microservices using TypeScript and AWS to handle millions of requests daily (<150ms response times)",
      "Reduced page refreshes by 50% in Shopify and BigCommerce integrations",
      "Delivered enterprise-ready integrations for WooCommerce and Magento, improving user experiences for shipping and product protection services"
    ],
    technologies: ["TypeScript", "PHP", "AWS Lambda", "DynamoDB", "API Gateway", "Shopify", "BigCommerce", "WooCommerce", "Magento"]
  },
  {
    title: "Solution Engineer",
    company: "Extend",
    period: "2020 - 2021",
    description: [
      "Optimized WooCommerce applications, increasing merchant integration speed by 75%",
      "Successfully onboarded 100+ merchants, improving conversion rates by 20%",
      "Developed an onboarding program that reduced new hire readiness time by 30%"
    ],
    technologies: ["JavaScript", "React", "Redux", "PHP", "WooCommerce"]
  },
  {
    title: "Software Engineer",
    company: "Skorit",
    period: "2020 - 2021",
    description: [
      "Built a responsive mobile web app and an admin panel, ensuring seamless operation with 1,000+ concurrent users",
      "Deployed a full-stack system on AWS, ensuring scalability and reliability"
    ],
    technologies: ["React", "Redux", "AWS EC2", "AWS RDS", "MySQL", "Node.js"]
  },
  {
    title: "Web Developer",
    company: "ElectriQ Marketing",
    period: "2019",
    description: [
      "Redesigned the company's website, migrating it from Squarespace to Webflow, improving performance by 50%"
    ],
    technologies: ["Webflow CMS", "HTML", "CSS", "JavaScript"]
  }
];

const TimelineSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      setStartX(e.pageX);
      setStartScrollLeft(container.scrollLeft);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleMouseLeave = () => {
      setIsDragging(false);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      
      const x = e.pageX;
      const walk = x - startX;
      if (container) {
        container.scrollLeft = startScrollLeft - walk;
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, startX, startScrollLeft]);

  return (
    <Container >
      <section className="timeline-section" id="experience">
        <div className="timeline-header">
          <h2>Professional Experience</h2>
        </div>
        <div 
          className={`timeline-container ${isDragging ? 'grabbing' : 'grab'}`} 
          ref={containerRef}
        >
          <div className="timeline-items-wrapper">
            {experiences.map((experience, index) => (
              <TimelineItem
                key={index}
                {...experience}
                isLeft={index % 2 === 0}
              />
            ))}
          </div>
        </div>
      </section>
    </Container>
  );
};

export default TimelineSection;
